import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSocket } from "../context/SocketContext";
import toastError from "../errors/toastError";
import api, { markUserAsInactive } from "../services/api";
import { loadFeatureFlags } from "../helpers/featureFlags";

const useAuth = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isAuth, setIsAuth] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({});
    const { socket, connectSocket } = useSocket();

    const [sessionExpired, setSessionExpired] = useState(false);

    const handleLogout = useCallback(async () => {
        try {
            await api.delete("/auth/logout");
        } catch (err) {
            console.error("Erro ao fazer logout:", err);
        }
        
        if (socket) {
            socket.emit("logout");
            socket.disconnect();
        }
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setIsAuth(false);
        setUser({});
        navigate("/login");
    }, [navigate, socket]);

    const handleSessionExpiredConfirm = useCallback(async () => {
        setSessionExpired(false);
        await handleLogout();
    }, [handleLogout]);

    useEffect(() => {
        const onSessionExpired = () => setSessionExpired(true);
        window.addEventListener("session:expired", onSessionExpired);
        return () => window.removeEventListener("session:expired", onSessionExpired);
    }, []);

    useEffect(() => {
        if (!socket) return;
        const onUserSessionExpired = (data) => {
            const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
            if (storedUser.id && String(data.userId) === String(storedUser.id)) {
                setSessionExpired(true);
            }
        };
        socket.on("userSessionExpired", onUserSessionExpired);
        return () => socket.off("userSessionExpired", onUserSessionExpired);
    }, [socket]);

    const [userInactive, setUserInactive] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            if (userInactive) {
                setLoading(false);
                return;
            }

            const tokenStr = localStorage.getItem("token");
            if (tokenStr) {
                try {
                    const token = JSON.parse(tokenStr);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                    
                    try {
                        const userStr = localStorage.getItem("user");
                        if (userStr) {
                            const storedUser = JSON.parse(userStr);
                            setUser(storedUser);
                            setIsAuth(true);
                        }
                    } catch (parseErr) {
                        console.error("Erro ao carregar usuário do localStorage:", parseErr);
                    }

                    const { data } = await api.post("/auth/refresh_token");
                    api.defaults.headers.Authorization = `Bearer ${data.token}`;
                    localStorage.setItem("token", JSON.stringify(data.token));
                    localStorage.setItem("user", JSON.stringify(data.user));
                    setIsAuth(true);
                    setUser(data.user);
                } catch (err) {
                    if (err.response && err.response.data && err.response.data.error === "ERR_USER_INACTIVE") {
                        setUserInactive(true);
                        markUserAsInactive();

                        toast.error(t("backendErrors.ERR_USER_INACTIVE") || "Este atendente está desativado!", {
                            autoClose: 10000,
                            onClose: () => {
                                navigate("/login");
                            }
                        });

                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                        setIsAuth(false);
                        setUser({});

                        delete api.defaults.headers.Authorization;
                    } else {
                        console.error("Erro ao atualizar token:", err);
                        handleLogout();
                    }
                }
            }
            setLoading(false);
        };

        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listener para atualização do usuário via socket (mantém whatsapps/queues sincronizados)
    useEffect(() => {
        if (!socket || !isAuth) return;

        const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user) {
                setUser((prevUser) => {
                    if (data.user.id === prevUser.id) {
                        const updatedUser = { ...prevUser, ...data.user };
                        localStorage.setItem("user", JSON.stringify(updatedUser));
                        return updatedUser;
                    }
                    return prevUser;
                });
            }
        };

        socket.on("user", handleUserUpdate);

        return () => {
            socket.off("user", handleUserUpdate);
        };
    }, [socket, isAuth]);

    // Carrega as feature-flags desta instalação ao autenticar (gate da UI WABA).
    useEffect(() => {
        if (isAuth) loadFeatureFlags();
    }, [isAuth]);

    const handleLogin = async (userData) => {
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", userData);
            localStorage.setItem("token", JSON.stringify(data.token));
            localStorage.setItem("user", JSON.stringify(data.user));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            setUser(data.user);
            setIsAuth(true);
            connectSocket();
            toast.success(t("auth.toasts.success"));
            navigate("/tickets");
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    };

    return { isAuth, user, loading, handleLogin, handleLogout, sessionExpired, handleSessionExpiredConfirm };
};

export default useAuth;
